import type { Plan, Profile } from './types';

export function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export async function hashInviteCode(code: string): Promise<string> {
  const normalized = code.trim().toUpperCase();
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const data = new TextEncoder().encode(`twyme-invite:${normalized}`);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Fallback for non-secure contexts (demo)
  let h = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    h = (h << 5) - h + normalized.charCodeAt(i);
    h |= 0;
  }
  return `fallback_${Math.abs(h).toString(16)}`;
}

export function generateInviteCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i += 1) {
    code += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return code;
}

export function createPlanDefaults(input: {
  spaceId: string;
  ownerId: string;
  title: string;
  planDate?: string | null;
}): Plan {
  const ts = nowIso();
  const clientId = createId();
  return {
    id: clientId,
    spaceId: input.spaceId,
    ownerId: input.ownerId,
    title: input.title.trim() || 'Untitled',
    description: '',
    planDate: input.planDate ?? null,
    startTime: null,
    endTime: null,
    isAllDay: true,
    availabilityStatus: 'none',
    visibility: 'shared',
    hideDetailsFromPartner: false,
    status: 'active',
    colorKey: null,
    categoryColor: null,
    createdAt: ts,
    updatedAt: ts,
    deletedAt: null,
    clientId,
    clientUpdatedAt: ts,
  };
}

export function softDeletePlan(plan: Plan): Plan {
  const ts = nowIso();
  return {
    ...plan,
    deletedAt: ts,
    updatedAt: ts,
    clientUpdatedAt: ts,
  };
}

export function restorePlan(plan: Plan): Plan {
  const ts = nowIso();
  return {
    ...plan,
    deletedAt: null,
    updatedAt: ts,
    clientUpdatedAt: ts,
  };
}

export function duplicatePlan(plan: Plan): Plan {
  const ts = nowIso();
  const clientId = createId();
  return {
    ...plan,
    id: clientId,
    clientId,
    title: plan.title,
    createdAt: ts,
    updatedAt: ts,
    clientUpdatedAt: ts,
    deletedAt: null,
  };
}

export function partnerOf(
  profiles: Profile[],
  currentUserId: string,
): Profile | null {
  return profiles.find((p) => p.id !== currentUserId) ?? null;
}
